import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInitials, useIsAdmin, useStore, useT } from "../store/useStore";
import { Avatar, Chip, Icon } from "../components/ui";
import { api } from "../api/http";
import { useVerwaltet } from "../lib/useVerwaltet";

interface MenuItem {
  icon: string;
  label: string;
  act: () => void;
  danger?: boolean;
}

export function AvatarMenu() {
  const t = useT();
  const auth = useStore((s) => s.auth);
  const setAuth = useStore((s) => s.setAuth);
  const initials = useInitials();
  const isAdmin = useIsAdmin();
  const { darf: verwaltet } = useVerwaltet();
  const de = useStore((s) => s.lang) === "de";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (!auth) return null;

  const logout = () => {
    void api.logout();
    setAuth(null);
    navigate("/");
  };

  const items: MenuItem[] = [
    { icon: "user-circle", label: t("menu.account"), act: () => navigate("/account") },
    { icon: "question", label: t("menu.help"), act: () => navigate("/legal/contact") },
    ...(isAdmin
      ? [{ icon: "shield-star", label: t("menu.admin"), act: () => navigate("/admin") }]
      : verwaltet
        ? [{ icon: "shield-star", label: de ? "Projektverwaltung" : "Project settings", act: () => navigate("/admin") }]
        : []),
    { icon: "sign-out", label: t("menu.logout"), act: logout, danger: true },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="avatar-pille"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "4px 6px 4px 12px",
          borderRadius: 999,
          background: "var(--field)",
          border: "1px solid var(--field-border)",
          cursor: "pointer",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }} className="hide-xs">
          {auth.displayName.split(" ")[0]}
        </span>
        <Avatar initials={initials} photo={auth.photoUrl} size={30} />
      </button>
      {open && (
        <div
          className="anim-pop"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            width: 230,
            borderRadius: 15,
            padding: 7,
            boxShadow: "var(--shadow-lg)",
            zIndex: 60,
            background: "var(--panel-solid)",
            border: "1px solid var(--glass-border-strong)",
          }}
        >
          <div style={{ padding: "10px 12px 12px", display: "flex", gap: 11, alignItems: "center" }}>
            <Avatar initials={initials} photo={auth.photoUrl} size={38} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{auth.displayName}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                <Chip tone={isAdmin ? "teal" : "neutral"} style={{ padding: "2px 8px", fontSize: 10.5 }}>
                  {t("role." + auth.role)}
                </Chip>
              </div>
            </div>
          </div>
          <div style={{ height: 1, background: "var(--glass-border)", margin: "2px 0 5px" }} />
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => {
                it.act();
                setOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 14,
                fontWeight: 500,
                color: it.danger ? "var(--error)" : "var(--text-dim)",
                transition: "background .12s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = it.danger ? "var(--error-soft)" : "var(--hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <Icon name={it.icon} size={18} />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
